import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';
import COLOR_ORDINAL from '../COLOR_ORDINAL';
import styles from './styles.module.scss'
import ChartNote from '../ChartNote';
import STATISTIC_TYPES, { FIND_STATISTIC_TYPE_BY_VALUE} from '../STATISTIC_TYPES';
import formatNumber from '../../utils/formatNumber';

const MOCK_DATA = [
    {
        name: null,
        data: [
            {
                name: "沒有",
                value: "0.9329",
            },
            {
                name: "有",
                value: "0.0546",
            },
            {
                name: "不記得",
                value: "0.0125",
            },
        ],
    },
];

// statisticsType 1: 百分比 2. 實際數量

function BarChart({
    data = MOCK_DATA,
    statisticsType = 1,
    chartTitle = "",
    chart2Title = "",
    chartNote = "",
}) {
    const svgRef = useRef(null);

    useEffect(() => {
        if (!data || data.length === 0) return;

        const chartData = data[0].data;

        // 清除舊的圖表
        d3.select(svgRef.current).selectAll("*").remove();

        // 設定圖表尺寸和邊距
        // 設定圖表尺寸和邊距
        const margin = { top: 20, right: 100, bottom: 40, left: 240 };
        const BAR_HEIGHT = 40;
        const BAR_PADDING = 12;
        const height = (BAR_HEIGHT + BAR_PADDING) * data[0].data.length;
        const width =
            Math.min(window.innerWidth, 1440) - 180 - 180 - margin.left - margin.right;

        // 創建 SVG
        const svg = d3
            .select(svgRef.current)
            .attr("width", width + margin.left + margin.right)
            .attr("height", height + margin.top + margin.bottom)
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);

        // 設定 y 軸比例尺
        const y = d3
            .scaleBand()
            .domain(chartData.map((d) => d.name))
            .range([0, height])
            .padding(BAR_PADDING / (BAR_HEIGHT + BAR_PADDING)); // 根據固定高度調整 padding

        // 設定 x 軸比例尺 (原本的 y 軸)
        const x = d3
            .scaleLinear()
            .domain([
                0,
                statisticsType === STATISTIC_TYPES.百分比.value
                    ? 1
                    : d3.max(chartData, (d) => parseFloat(d.value)),
            ])
            .range([0, width])
            .nice();

        // 獲取 x 軸的刻度值
        const xTicks = x.ticks(10).map((d) => d); // 使用實際數值而不是格式化的刻度

        // 設定顏色比例尺
        const color = d3
            .scaleOrdinal()
            .domain(chartData.map((d) => d.name))
            .range(COLOR_ORDINAL);

        // 添加垂直背景條紋
        svg.selectAll(".background-stripe")
            .data(xTicks)
            .join("rect")
            .attr("class", "background-stripe")
            .attr("x", (d, i) => x(xTicks[i]))
            .attr("y", 0)
            .attr("width", (d, i) => {
                if (i === xTicks.length - 1) return 0;
                return x(xTicks[i + 1]) - x(xTicks[i]);
            })
            .attr("height", height)
            .attr("fill", (d, i) => (i % 2 === 0 ? "#F9F9F9" : "#fff"))
            .attr("opacity", 0.4)
            .attr("stroke", "#D2D2D2")
            .attr("stroke-width", 1);

        // 繪製水平長條圖
        svg.selectAll(".bar")
            .data(chartData)
            .join("rect")
            .attr("class", "bar")
            .attr("y", (d) => y(d.name))
            .attr("x", 0)
            .attr("height", y.bandwidth())
            .attr("width", (d) => x(parseFloat(d.value)))
            .attr("fill", (d) => color(d.name));

        // 添加 y 軸 (原本的 x 軸)
        svg.append("g")
            .call(d3.axisLeft(y))
            .selectAll("text")
            .style("text-anchor", "end")
            .style("font-size", "15px")
            .each(function (d) {
                const text = d3.select(this);
                if (!d) {
                    text.text("");
                    return;
                }
                if (d.includes("~")) {
                    const parts = d.split("~");
                    text.text("")
                        .append("tspan")
                        .text(parts[0] + "~")
                        .attr("x", -10)
                        .attr("dy", 0);
                    text.append("tspan")
                        .text(parts[1])
                        .attr("x", -10)
                        .attr("dy", "1.2em");
                } else if (d.includes("【訪員請紀錄】")) {
                    const parts = d.split("【訪員請紀錄】");
                    text.text("")
                        .append("tspan")
                        .text(parts[0])
                        .attr("x", -10)
                        .attr("dy", 0);
                    text.append("tspan")
                        .text("【訪員請紀錄】" + parts[1])
                        .attr("x", -10)
                        .attr("dy", "1.2em");
                } else if (d.length > 6) {
                    const midpoint = Math.ceil(d.length / 2);
                    const line1 = d.slice(0, midpoint);
                    const line2 = d.slice(midpoint);
                    text.text("");
                    text.append("tspan")
                        .text(line1)
                        .attr("x", -10)
                        .attr("dy", 0);
                    text.append("tspan")
                        .text(line2)
                        .attr("x", -10)
                        .attr("dy", "1.2em");
                }
            });

        // 添加 x 軸 (原本的 y 軸)
        svg.append("g")
            .attr("transform", `translate(0,${height})`)
            .call(
                d3
                    .axisBottom(x)
                    .ticks(10)
                    .tickFormat((d) =>
                        statisticsType === STATISTIC_TYPES.百分比.value
                            ? d3.format(".0%")(d)
                            : d
                    )
            );

        // 添加數值標籤
        svg.selectAll(".value-label")
            .data(chartData)
            .join("text")
            .attr("class", "value-label")
            .attr("x", (d) => x(parseFloat(d.value)) + 5)
            .attr("y", (d) => y(d.name) + y.bandwidth() / 2)
            .attr("dy", "0.35em")
            .attr("text-anchor", "start")
            .text((d) => `${formatNumber(d.value, statisticsType)}`);
    }, [data, statisticsType]);

    return (
        <div
            style={{ width: "100%", height: "100%" }}
            className="chart-container bar-chart"
        >
            <div className={styles.barChart}>
                <svg ref={svgRef}></svg>
                {chartNote && (
                    <ChartNote chartNote={chartNote} />
                )}
            </div>
        </div>
    );
}

export default BarChart;